import type { UserRole } from '@fluenceur/types';
import * as Sentry from '@sentry/react';

// Type definitions for better type safety
interface SentryUser {
  id: string;
  username?: string;
  role: UserRole;
}

interface BreadcrumbData {
  [key: string]: unknown;
}

/**
 * Set user context for Sentry with enhanced privacy
 * Only sets non-PII data (id, username, role) - never email
 */
export function setSentryUser(user: SentryUser | null) {
  if (user) {
    // Only set non-PII user data
    Sentry.setUser({
      id: user.id,
      username: user.username,
      // Custom attributes (non-PII)
      role: user.role,
    });

    // Add user context for better debugging
    Sentry.setContext('user_metadata', {
      role: user.role,
      hasUsername: !!user.username,
    });
  } else {
    Sentry.setUser(null);
    Sentry.setContext('user_metadata', null);
  }
}

/**
 * Add breadcrumb for tracking user actions with data sanitization
 */
export function addSentryBreadcrumb(
  message: string,
  category: string,
  level: Sentry.SeverityLevel = 'info',
  data?: BreadcrumbData
) {
  // Sanitize data to remove sensitive information
  const sanitizedData = data ? sanitizeData(data) : undefined;

  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data: sanitizedData,
    timestamp: Date.now(),
  });
}

/**
 * Capture a message in Sentry with additional context
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = 'info',
  context?: Record<string, unknown>
) {
  if (context) {
    Sentry.withScope((scope) => {
      scope.setContext('message_context', sanitizeData(context));
      Sentry.captureMessage(message, level);
    });
  } else {
    Sentry.captureMessage(message, level);
  }
}

/**
 * Capture an exception in Sentry with enhanced context
 */
export function captureException(error: Error | unknown, context?: Record<string, unknown>) {
  // In development, log to console as well for debugging
  if (import.meta.env.DEV) {
    console.error('Sentry exception:', error, context);
  }

  if (context) {
    Sentry.withScope((scope) => {
      scope.setContext('additional', sanitizeData(context));
      scope.setTag('has_context', true);
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
}

/**
 * Track user interactions with enhanced context
 */
export function trackInteraction(action: string, category: string, data?: Record<string, unknown>) {
  addSentryBreadcrumb(`User ${action}`, `user.${category}`, 'info', data);
}

/**
 * Start a transaction for performance monitoring
 */
export function startTransaction(name: string, op: string) {
  return Sentry.startSpan({ name, op }, () => {
    // Transaction logic here
  });
}

/**
 * Sanitize data to remove sensitive information
 */
function sanitizeData(data: Record<string, unknown>): Record<string, unknown> {
  const sensitiveKeys = [
    'password',
    'token',
    'secret',
    'apiKey',
    'api_key',
    'creditCard',
    'credit_card',
    'ssn',
    'email',
    'phone',
  ];

  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(data)) {
    const lowerKey = key.toLowerCase();

    if (sensitiveKeys.some((sensitive) => lowerKey.includes(sensitive))) {
      sanitized[key] = '[FILTERED]';
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      sanitized[key] = sanitizeData(value as Record<string, unknown>);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
}
